import { ImgWithFallback } from "@/components/img-with-fallback";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { VariantPicker } from "@/components/variant-picker";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { X, Loader2, Sparkles, Play, StopCircle, Flame, Image as ImageIcon, Settings as SettingsIcon, Wand2, Users, Package, Tag, Folder, Crop, History, Trash2, Check, Heart, MessageCircle, Share2, MoreHorizontal, ThumbsUp, Bookmark, RotateCw, LayoutGrid, Download, ArrowLeft, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Link, useParams } from "wouter";
import type { Brand, Persona, Product, NarrativeAngle, Offer, Project, CtVariationImage } from "@shared/schema";
import { type AspectRatio, AD_ASPECT_RATIO_OPTIONS } from "@shared/aspectRatios";
import { SafeZoneOverlay, SafeZoneToggle } from "@/components/safe-zone-overlay";
import { VariationSetResults } from "@/components/variation-set-results";
import { loadLastAspectRatio, saveLastAspectRatio } from "@/lib/aspectRatio";
import { usePickerSearch, PickerSearchHeader, PickerNoMatches } from "@/lib/picker-search";
import { thumbUrl, thumbFallback } from "@/lib/img-thumb";
import { AdImage } from "@/components/ad-image";

type CtVariationSetWithImages = {
  id: string;
  sessionId: string;
  sourceCardId: string;
  status: string;
  aspectRatio: string | null;
  originalImageUrl: string | null;
  error: string | null;
  keptLockedAt: string | Date | null;
  images: CtVariationImage[];
};

type CtSettings = {
  enabled: boolean;
  defaultBatchSize: number;
  prefetchThreshold: number;
  maxConcurrentRenders: number;
  endpoints: Record<string, string>;
  defaults: { aspectRatio: AspectRatio; mockMode: boolean };
};

type CtCard = {
  id: string;
  sessionId: string;
  batchId: string;
  status: string;
  concept: any;
  prompt: any;
  render: { imageUrl?: string; thumbnailUrl?: string; error?: string } | null;
};

type CtSession = {
  id: string;
  name?: string;
  status: string;
  inputs: { brandId?: string; personaId?: string; productId?: string; narrativeAngleId?: string; offerId?: string; projectId?: string; aspectRatio: AspectRatio };
  learningFile: any;
  stats: any;
};

export default function CreativeTinderPage() {
  const { toast } = useToast();
  const { data: settings } = useQuery<CtSettings>({ queryKey: ["/api/creative-tinder/settings"] });

  // ----- Inputs -----
  const params = useParams<{ brandId?: string }>();
  const routeBrandId = params?.brandId ?? "";
  const { data: brands } = useQuery<Brand[]>({ queryKey: ["/api/brands"], enabled: !routeBrandId });
  const [brandIdState, setBrandIdState] = useState<string>("");
  const brandId = routeBrandId || brandIdState;
  const setBrandId = (v: string) => { if (!routeBrandId) setBrandIdState(v); };
  const { data: activeBrand } = useQuery<Brand>({ queryKey: [`/api/brands/${brandId}`], enabled: !!brandId });
  const { data: personas } = useQuery<Persona[]>({ queryKey: [`/api/brands/${brandId}/personas`], enabled: !!brandId });
  const { data: products } = useQuery<Product[]>({ queryKey: [`/api/brands/${brandId}/products`], enabled: !!brandId });
  const { data: angles } = useQuery<NarrativeAngle[]>({ queryKey: [`/api/brands/${brandId}/narrative-angles`], enabled: !!brandId });
  const { data: offers } = useQuery<Offer[]>({ queryKey: [`/api/brands/${brandId}/offers`], enabled: !!brandId });
  const { data: projects } = useQuery<Project[]>({ queryKey: [`/api/brands/${brandId}/projects`], enabled: !!brandId });

  const [personaId, setPersonaId] = useState<string>("");
  const [productId, setProductId] = useState<string>("");
  // Task #744 — optional product variant selection (null = no specific variant).
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(null);
  const [narrativeAngleId, setNarrativeAngleId] = useState<string>("");
  const [offerId, setOfferId] = useState<string>("");
  const [projectId, setProjectId] = useState<string>("");
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(() => loadLastAspectRatio());
  // Whether to bake the brand logo into every ad in this deck. ON by
  // default = legacy behaviour. Applies to the whole session.
  const [includeLogo, setIncludeLogo] = useState<boolean>(true);

  // ----- Session state -----
  const [session, setSession] = useState<CtSession | null>(null);
  const [cards, setCards] = useState<CtCard[]>([]);
  const [debugBatch, setDebugBatch] = useState<{ rawResponse: any; requestPayload: any; endpoint: string | null; mock: boolean } | null>(null);
  const [batchError, setBatchError] = useState<{ message: string; requestPayload?: any; rawResponse?: any; endpoint?: string } | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const renderingRef = useRef<Set<string>>(new Set());
  const fetchingBatchRef = useRef(false);
  const promptingRef = useRef<Set<string>>(new Set());
  const retryRef = useRef<Map<string, { prompt: number; render: number }>>(new Map());
  const [promptErrors, setPromptErrors] = useState<Record<string, string>>({});
  // Drag-to-swipe state for the phone mockup.
  const [dragDx, setDragDx] = useState(0);
  const [exitDir, setExitDir] = useState<null | "left" | "right">(null);
  const dragRef = useRef<{ startX: number; active: boolean }>({ startX: 0, active: false });
  // ----- Review mode -----
  // When the deck is exhausted the user can either generate 30 more concepts
  // or switch into review mode: the phone disappears and every saved ad moves
  // into a roomy card gallery where they can hand-pick a "set" to download.
  const [reviewMode, setReviewMode] = useState(false);
  const [selectedSetIds, setSelectedSetIds] = useState<Set<string>>(new Set());
  const batchSize = settings?.defaultBatchSize ?? 30;

  // ----- Create sets (variation generator) -----
  const { user } = useAuth();
  const isMaster = !!(user as any)?.isMaster || !!(user as any)?.isSuperuser;
  const [setsMode, setSetsMode] = useState(false);
  const [variationSets, setVariationSets] = useState<CtVariationSetWithImages[]>([]);
  const [creatingSets, setCreatingSets] = useState(false);

  const selectedPersona = useMemo(() => (personas ?? []).find((p) => p.id === personaId) || null, [personas, personaId]);
  const selectedProduct = useMemo(() => (products ?? []).find((p) => p.id === productId) || null, [products, productId]);
  const selectedAngle = useMemo(() => (angles ?? []).find((a) => a.id === narrativeAngleId) || null, [angles, narrativeAngleId]);
  const selectedOffer = useMemo(() => (offers ?? []).find((o) => o.id === offerId) || null, [offers, offerId]);
  const selectedProject = useMemo(() => (projects ?? []).find((p) => p.id === projectId) || null, [projects, projectId]);

  const createSession = useMutation({
    mutationFn: async () => {
      setBatchError(null);
      setDebugBatch(null);
      const r = await apiRequest("POST", "/api/creative-tinder/sessions", {
        name: `Batch ${new Date().toLocaleString()}`,
        inputs: {
          brandId,
          aspectRatio,
          includeLogo,
          persona: selectedPersona,
          product: selectedProduct,
          selectedVariantName,
          offer: selectedOffer,
          project: selectedProject,
          mode: "instant",
        },
        brandContext: activeBrand ?? null,
      });
      return r.json() as Promise<CtSession>;
    },
    onSuccess: async (s) => {
      setSession(s);
      setCards([]);
      retryRef.current.clear();
      setReviewMode(false);
      setSelectedSetIds(new Set());
      setSetsMode(false);
      setVariationSets([]);
      await generateBatch(s.id);
    },
    onError: (e: any) => toast({ title: "Couldn't start session", description: e?.message ?? "Unknown error", variant: "destructive" }),
  });

  const endSession = useMutation({
    mutationFn: async () => {
      if (!session) return;
      const r = await apiRequest("POST", `/api/creative-tinder/sessions/${session.id}/end`);
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Session ended" });
      setSession(null);
      setCards([]);
      retryRef.current.clear();
      setReviewMode(false);
      setSelectedSetIds(new Set());
      setSetsMode(false);
      setVariationSets([]);
      queryClient.invalidateQueries({ queryKey: ["/api/creative-tinder/sessions"] });
    },
  });

  // ----- Saved sessions (list / resume / delete) -----
  // No auto-resume on mount — the page always defaults to "ready to start a new
  // session". Users explicitly pick a past session from the dropdown to resume.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const { data: savedSessions = [] } = useQuery<CtSession[]>({
    queryKey: ["/api/creative-tinder/sessions"],
    queryFn: async () => {
      const r = await apiRequest("GET", "/api/creative-tinder/sessions");
      return r.json();
    },
  });

  // Only sessions for the currently-selected brand.
  const brandSessions = useMemo(
    () => savedSessions.filter((s) => (s.inputs as any)?.brandId === brandId),
    [savedSessions, brandId],
  );

  function hydrateFromSession(s: CtSession, fetchedCards: CtCard[]) {
    const inp = s.inputs as any;
    setPersonaId(inp?.persona?.id ?? "");
    setProductId(inp?.product?.id ?? "");
    setSelectedVariantName(inp?.selectedVariant?.name ?? inp?.selectedVariantName ?? null);
    setNarrativeAngleId(inp?.narrativeAngle?.id ?? "");
    setOfferId(inp?.offer?.id ?? "");
    setProjectId(inp?.project?.id ?? "");
    if (inp?.aspectRatio) setAspectRatio(inp.aspectRatio as AspectRatio);
    setIncludeLogo(inp?.includeLogo !== false);
    setSession(s);
    setCards(fetchedCards);
    setBatchError(null);
    setDebugBatch(null);
    setPromptErrors({});
    retryRef.current.clear();
    promptingRef.current.clear();
    renderingRef.current.clear();
    setReviewMode(false);
    setSelectedSetIds(new Set());
    setSetsMode(false);
    setVariationSets([]);
  }

  const resumeSession = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiRequest("GET", `/api/creative-tinder/sessions/${id}`);
      return r.json() as Promise<{ session: CtSession; cards: CtCard[] }>;
    },
    onSuccess: async ({ session: s, cards: fetchedCards }) => {
      hydrateFromSession(s, fetchedCards);
      toast({ title: "Session resumed", description: `${fetchedCards.length} card${fetchedCards.length === 1 ? "" : "s"} restored` });
      if (isMaster) {
        try {
          const sets = await fetchVariationSets(s.id);
          if (sets.length > 0) {
            setVariationSets(sets);
            setSetsMode(true);
          }
        } catch { /* non-fatal */ }
      }
    },
    onError: (e: any) => toast({ title: "Couldn't resume", description: e?.message ?? "Unknown error", variant: "destructive" }),
  });

  const deleteSession = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiRequest("DELETE", `/api/creative-tinder/sessions/${id}`);
      return r.json();
    },
    onSuccess: (_data, id) => {
      toast({ title: "Session deleted" });
      if (session?.id === id) {
        setSession(null);
        setCards([]);
        retryRef.current.clear();
        setReviewMode(false);
        setSelectedSetIds(new Set());
        setSetsMode(false);
        setVariationSets([]);
      }
      setConfirmDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/creative-tinder/sessions"] });
    },
    onError: (e: any) => toast({ title: "Couldn't delete", description: e?.message ?? "Unknown error", variant: "destructive" }),
  });

  // Concept batch generation. Fire-and-forget against the non-streaming
  // /batches endpoint, then poll /batches/:batchId/status every 3s until the
  // job finishes. The streaming SSE path has been removed because it didn't
  // play well with the Replit edge proxy.
  // Instant batch: server synchronously creates N cards seeded with random
  // global static ad units. Each card then auto-fires through the
  // New Prompt Maker → gpt-image-2 pipeline via the auto-fire effects below.
  async function generateBatch(sid: string) {
    if (fetchingBatchRef.current) return;
    fetchingBatchRef.current = true;
    setBatchLoading(true);
    setBatchError(null);
    try {
      const batchSize = settings?.defaultBatchSize ?? 30;
      const r = await apiRequest("POST", `/api/creative-tinder/sessions/${sid}/instant-batches`, { batchSize });
      const json: any = await r.json();
      if (!r.ok) throw new Error(json?.message || `Failed to create batch (${r.status})`);
      const newCards = (json.cards ?? []) as CtCard[];
      if (newCards.length === 0) throw new Error("Batch returned no cards.");
      setCards((prev) => [...prev, ...newCards]);
    } catch (e: any) {
      setBatchError({ message: e?.message ?? "Unknown error" });
      toast({ title: "Couldn't generate batch", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      fetchingBatchRef.current = false;
      setBatchLoading(false);
    }
  }

  // Generate another batch of 30 random templates/concepts into the current
  // session, then drop the user back onto the swipe deck.
  async function generateMore() {
    if (!session) return;
    setReviewMode(false);
    await generateBatch(session.id);
  }

  function toggleSetSelection(id: string) {
    setSelectedSetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function downloadOne(url: string, name: string) {
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name || "ad").replace(/[^a-z0-9-_]+/gi, "-").slice(0, 60)}.png`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // ----- Create sets (variation generator) -----
  const SET_TERMINAL = new Set(["complete", "partial", "failed"]);
  const setsGenerating = useMemo(
    () => variationSets.some((s) => !SET_TERMINAL.has(s.status)),
    [variationSets],
  );

  async function fetchVariationSets(sid: string): Promise<CtVariationSetWithImages[]> {
    const r = await apiRequest("GET", `/api/creative-tinder/sessions/${sid}/variation-sets`);
    const json: any = await r.json();
    return (json?.sets ?? []) as CtVariationSetWithImages[];
  }

  async function createSets() {
    if (!session || creatingSets) return;
    const ids = Array.from(selectedSetIds);
    if (ids.length === 0) return;
    setCreatingSets(true);
    try {
      const r = await apiRequest("POST", `/api/creative-tinder/sessions/${session.id}/variation-sets`, { cardIds: ids });
      const json: any = await r.json();
      if (!r.ok) throw new Error(json?.message || `Failed to start sets (${r.status})`);
      setVariationSets((json.sets ?? []) as CtVariationSetWithImages[]);
      setSetsMode(true);
      toast({ title: "Generating sets", description: `${json.createdSetIds?.length ?? ids.length} set${(json.createdSetIds?.length ?? ids.length) === 1 ? "" : "s"} started` });
    } catch (e: any) {
      toast({ title: "Couldn't create sets", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setCreatingSets(false);
    }
  }

  // Task #744 — clear a stale variant pick when the product changes.
  useEffect(() => {
    setSelectedVariantName(null);
  }, [productId]);

  // Poll while any set is still generating (also keeps a freshly-resumed
  // session's sets up to date).
  useEffect(() => {
    if (!session || !setsGenerating) return;
    const sid = session.id;
    const t = setInterval(async () => {
      try {
        setVariationSets(await fetchVariationSets(sid));
      } catch { /* transient — keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [session?.id, setsGenerating]);

  // ----- Prompt Maker (per-card) -----
  async function generatePrompt(cardId: string) {
    if (promptingRef.current.has(cardId)) return;
    promptingRef.current.add(cardId);
    setPromptErrors((prev) => { const n = { ...prev }; delete n[cardId]; return n; });
    setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, status: "prompting" } : c)));
    try {
      const r = await apiRequest("POST", `/api/creative-tinder/cards/${cardId}/prompt`);
      const text = await r.text();
      let data: any;
      try { data = JSON.parse(text); } catch { throw new Error(`Non-JSON response: ${text.slice(0, 300)}`); }
      if (!r.ok) throw new Error(data?.message || `Prompt request failed (${r.status})`);
      // Server returns the pending card; the polling effect below will pick up the final status.
      setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, ...data } : c)));
    } catch (e: any) {
      const msg = e?.message ?? "Unknown error";
      setPromptErrors((prev) => ({ ...prev, [cardId]: msg }));
      setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, status: "prompt_failed" } : c)));
      promptingRef.current.delete(cardId);
      toast({ title: "Prompt Maker error", description: msg, variant: "destructive" });
    }
  }

  async function generateAllPrompts() {
    const queue = cards
      .filter((c) => c.status === "concept_created" || c.status === "prompt_failed")
      .map((c) => c.id);
    const concurrency = 3;
    let idx = 0;
    const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (idx < queue.length) {
        const cardId = queue[idx++];
        await generatePrompt(cardId);
      }
    });
    await Promise.all(workers);
  }

  // Poll cards that are in "prompting" status until they reach a terminal state.
  // Uses a ref-backed snapshot so the interval is NOT torn down every time any
  // card's state changes (renders advancing constantly would otherwise reset
  // the timer before it ever fires, stranding prompting cards).
  const cardsRef = useRef<CtCard[]>(cards);
  useEffect(() => { cardsRef.current = cards; }, [cards]);
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(async () => {
      const inFlight = cardsRef.current.filter(
        (c) => c.status === "prompting" || c.status === "rendering" || c.status === "queued"
      );
      if (inFlight.length === 0) return;
      await Promise.all(inFlight.map(async (c) => {
        try {
          const r = await apiRequest("GET", `/api/creative-tinder/cards/${c.id}`);
          if (!r.ok) return;
          const fresh = await r.json();
          if (fresh.status !== c.status) {
            if (c.status === "prompting") promptingRef.current.delete(c.id);
            if (c.status === "rendering" || c.status === "queued") renderingRef.current.delete(c.id);
            if (fresh.status === "prompt_failed") {
              setPromptErrors((prev) => ({ ...prev, [c.id]: (fresh.prompt?.error) || "Prompt Maker failed" }));
            }
            setCards((prev) => prev.map((x) => (x.id === c.id ? fresh : x)));
          }
        } catch {}
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, [session]);

  // ----- Reactive prompt auto-fire -----
  // Fires generatePrompt for every card in "concept_created" in parallel.
  // Sim sync ceiling is 150 req/min / 300 burst, well above batch sizes.
  // Resilient to refresh / batches loaded via GET /sessions/:id.
  useEffect(() => {
    if (!session) return;
    const queue = cards.filter(
      (c) => c.status === "concept_created" && !promptingRef.current.has(c.id)
    );
    queue.forEach((c) => { generatePrompt(c.id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, session]);

  // ----- Reactive render auto-fire -----
  // Fires renderCard for every card in prompt_ready in parallel. No cap.
  async function renderCard(cardId: string) {
    if (renderingRef.current.has(cardId)) return;
    renderingRef.current.add(cardId);
    setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, status: "rendering" } : c)));
    try {
      // Server returns 202 immediately and runs Fal in the background.
      // We don't await the full render here — the polling effect above will
      // pick up the final status. Keeping `renderingRef` set ensures we don't
      // double-fire, and polling clears it when status flips off "rendering".
      const r = await apiRequest("POST", `/api/creative-tinder/cards/${cardId}/render`);
      if (!r.ok && r.status !== 202) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data?.message || `Render failed (${r.status})`);
      }
    } catch (e: any) {
      setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, status: "failed", render: { error: e?.message ?? "Render failed" } } : c)));
      renderingRef.current.delete(cardId);
    }
  }
  useEffect(() => {
    if (!session) return;
    cards
      .filter((c) => c.status === "prompt_ready" && !renderingRef.current.has(c.id))
      .forEach((c) => { renderCard(c.id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, session]);

  // ----- Auto-retry single failures (max 2 attempts per stage) -----
  useEffect(() => {
    if (!session) return;
    cards.forEach((c) => {
      const counts = retryRef.current.get(c.id) ?? { prompt: 0, render: 0 };
      if (c.status === "prompt_failed" && !promptingRef.current.has(c.id) && counts.prompt < 2) {
        retryRef.current.set(c.id, { ...counts, prompt: counts.prompt + 1 });
        generatePrompt(c.id);
      } else if (c.status === "failed" && !renderingRef.current.has(c.id) && (c.prompt as any)?.renderPackage && counts.render < 2) {
        retryRef.current.set(c.id, { ...counts, render: counts.render + 1 });
        renderCard(c.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, session]);

  const inputsReady = !!(brandId && personaId && productId);

  const aspectClass = aspectRatio === "9:16" ? "aspect-[9/16]" : aspectRatio === "4:5" ? "aspect-[4/5]" : aspectRatio === "16:9" ? "aspect-[16/9]" : aspectRatio === "2:3" ? "aspect-[2/3]" : "aspect-square";

  // 9:16 → 4:5 safe-zone preview overlay (Task #819) — one session-level
  // toggle shared by the phone deck, the review gallery, and variation sets.
  const [showSafeZone, setShowSafeZone] = useState(false);

  // ----- Swipe queue derivation -----
  // The phone shows ONE card at a time. To prevent the on-screen ad from
  // swapping when a *new* card finishes rendering (server may return cards in
  // a different order each poll), we lock the display order to the order in
  // which cards first became rendered. New renders queue *behind* whatever
  // the user is currently looking at.
  const isPending = (c: CtCard) => c.status !== "swiped_left" && c.status !== "swiped_right";
  const renderOrderRef = useRef<string[]>([]);
  useEffect(() => {
    const seen = new Set(renderOrderRef.current);
    for (const c of cards) {
      if (c.render?.imageUrl && !seen.has(c.id)) {
        renderOrderRef.current.push(c.id);
        seen.add(c.id);
      }
    }
  }, [cards]);
  const swipeQueue = useMemo(() => {
    const byId = new Map(cards.map((c) => [c.id, c]));
    const ordered: CtCard[] = [];
    for (const id of renderOrderRef.current) {
      const c = byId.get(id);
      if (c && isPending(c) && c.render?.imageUrl) ordered.push(c);
    }
    return ordered;
  }, [cards]);
  const savedCards = useMemo(
    () => cards.filter((c) => c.status === "swiped_right"),
    [cards],
  );
  const currentCard = swipeQueue[0] ?? null;
  const nextCard = swipeQueue[1] ?? null;
  const totalPending = cards.filter(isPending).length; // includes still-rendering
  const renderingNow = cards.filter((c) => isPending(c) && !c.render?.imageUrl && c.status !== "failed").length;
  // Cards waiting for a global fal concurrency slot ("queued" server status).
  // A subset of renderingNow — displayed separately so the deck explains WHY
  // nothing is landing yet.
  const queuedNow = cards.filter((c) => isPending(c) && !c.render?.imageUrl && c.status === "queued").length;

  async function swipe(direction: "left" | "right", cardId?: string) {
    const id = cardId ?? currentCard?.id;
    if (!id || exitDir) return;
    const prevStatus = cards.find((c) => c.id === id)?.status;
    setExitDir(direction);
    // Animate out, then commit.
    setTimeout(() => {
      setCards((prev) => prev.map((c) => (c.id === id ? { ...c, status: direction === "right" ? "swiped_right" : "swiped_left" } : c)));
      setExitDir(null);
      setDragDx(0);
    }, 220);
    try {
      await apiRequest("POST", `/api/creative-tinder/cards/${id}/swipe`, { action: direction });
    } catch (e: any) {
      // Rollback the optimistic status change so the card re-enters the queue.
      if (prevStatus) {
        setCards((prev) => prev.map((c) => (c.id === id ? { ...c, status: prevStatus } : c)));
      }
      toast({ title: "Couldn't save swipe", description: e?.message ?? "Unknown error", variant: "destructive" });
    }
  }

  // Keyboard: ← reject, → save.
  useEffect(() => {
    if (!session) return;
    const onKey = (e: KeyboardEvent) => {
      if (reviewMode || !currentCard || exitDir) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement | null)?.isContentEditable) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); swipe("left"); }
      else if (e.key === "ArrowRight") { e.preventDefault(); swipe("right"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, currentCard?.id, exitDir, reviewMode]);

  // Pointer drag handlers (attached to the phone card).
  const onPointerDown = (e: React.PointerEvent) => {
    if (!currentCard || exitDir) return;
    dragRef.current = { startX: e.clientX, active: true };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    setDragDx(e.clientX - dragRef.current.startX);
  };
  const onPointerUp = (e?: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    if (e) {
      try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    }
    const dx = dragDx;
    if (dx > 110) swipe("right");
    else if (dx < -110) swipe("left");
    else setDragDx(0);
  };

  if (settings && !settings.enabled) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card><CardContent className="p-10 text-center space-y-3">
          <Flame className="h-10 w-10 mx-auto text-muted-foreground" />
          <h1 className="text-xl font-bold">Creative Tinder is disabled</h1>
          <p className="text-sm text-muted-foreground">Enable it in the Creative Tinder settings to start using the tool.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="px-6 pt-4 pb-6 max-w-7xl mx-auto space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Flame className="h-6 w-6 text-violet-500" /> Creative Tinder
          </h1>
          <p className="text-sm text-muted-foreground">Pick a persona + product, hit Generate, and swipe through 30 instant ad concepts pulled from random templates.{activeBrand?.name ? <span className="ml-1.5 font-medium text-foreground">· {activeBrand.name}</span> : null}</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="button-sessions-menu">
                <History className="h-4 w-4 mr-1.5" /> Sessions
                {brandSessions.length > 0 && (
                  <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-[10px]" data-testid="badge-session-count">
                    {brandSessions.length}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-[420px] overflow-y-auto">
              <DropdownMenuLabel className="text-xs">Saved sessions{activeBrand?.name ? ` — ${activeBrand.name}` : ""}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {brandSessions.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="text-no-sessions">
                  No saved sessions yet. Hit “Start session” to create one.
                </div>
              ) : (
                brandSessions.map((s) => {
                  const isActive = session?.id === s.id;
                  const ts = (s as any).createdAt ? new Date((s as any).createdAt).toLocaleString() : "";
                  return (
                    <DropdownMenuItem
                      key={s.id}
                      className="flex items-start gap-2 py-2 cursor-pointer"
                      onSelect={(e) => {
                        e.preventDefault();
                        if (isActive) return;
                        resumeSession.mutate(s.id);
                      }}
                      data-testid={`item-session-${s.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {isActive && <Check className="h-3 w-3 text-violet-600 shrink-0" />}
                          <span className="text-xs font-semibold truncate" data-testid={`text-session-name-${s.id}`}>{s.name || "Session"}</span>
                          <Badge
                            variant="outline"
                            className={`text-[9px] h-4 px-1 ${s.status === "active" ? "text-emerald-600 border-emerald-300" : "text-muted-foreground"}`}
                          >
                            {s.status}
                          </Badge>
                        </div>
                        {ts && <div className="text-[10px] text-muted-foreground mt-0.5">{ts}</div>}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-muted-foreground hover:text-red-600"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setConfirmDeleteId(s.id); }}
                        data-testid={`button-delete-session-${s.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuItem>
                  );
                })
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link href="/creative-tinder/settings">
            <Button variant="outline" size="sm" data-testid="link-settings">
              <SettingsIcon className="h-4 w-4 mr-1.5" /> Settings
            </Button>
          </Link>
        </div>
      </div>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={(open) => { if (!open) setConfirmDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this session?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the session, its concept cards, and your swipe history. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (confirmDeleteId) deleteSession.mutate(confirmDeleteId); }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="button-confirm-delete"
            >
              {deleteSession.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Inputs — full card pre-session; compact strip once a session is active so the phone is the focal point. */}
      {session ? (
        <Card className="border-border/60" data-testid="card-inputs-compact">
          <CardContent className="p-2.5 flex items-center gap-2 flex-wrap text-xs">
            {selectedPersona && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 pl-1 pr-2.5 py-1" data-testid="chip-persona">
                {(selectedPersona as any).imageUrl ? (
                  <img src={thumbUrl((selectedPersona as any).imageUrl, 64)!} onError={thumbFallback((selectedPersona as any).imageUrl)} alt="" className="h-5 w-5 rounded-full object-cover" />
                ) : (
                  <span className="h-5 w-5 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center"><Users className="h-3 w-3" /></span>
                )}
                <span className="font-medium truncate max-w-[180px]">{selectedPersona.name}</span>
              </span>
            )}
            {(products ?? []).find((p) => p.id === productId) && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1" data-testid="chip-product">
                <Package className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium truncate max-w-[180px]">{(products ?? []).find((p) => p.id === productId)?.name}</span>
              </span>
            )}
            {(angles ?? []).find((a) => a.id === narrativeAngleId) && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1" data-testid="chip-angle">
                <Sparkles className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium truncate max-w-[220px]">{(angles ?? []).find((a) => a.id === narrativeAngleId)?.name}</span>
              </span>
            )}
            {offerId && (offers ?? []).find((o) => o.id === offerId) && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1" data-testid="chip-offer">
                <Tag className="h-3 w-3 text-muted-foreground" />
                <span className="font-medium truncate max-w-[180px]">{(offers ?? []).find((o) => o.id === offerId)?.name}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full border border-border/70 px-2 py-0.5 text-[10px] text-muted-foreground">
              <Crop className="h-3 w-3" /> {aspectRatio}
            </span>
            {aspectRatio === "9:16" && (
              <SafeZoneToggle active={showSafeZone} onToggle={() => setShowSafeZone((v) => !v)} />
            )}
            <div className="ml-auto">
              <Button variant="outline" size="sm" onClick={() => endSession.mutate()} disabled={endSession.isPending} className="h-7 text-xs" data-testid="button-end">
                {endSession.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <StopCircle className="h-3 w-3 mr-1" />}
                End session
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
      <Card className="relative overflow-hidden p-4 space-y-3 border-border/60 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.12)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_-12px_rgba(0,0,0,0.6)] bg-gradient-to-b from-card to-muted/30 dark:from-card dark:to-background/40">
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/[0.06] blur-3xl" />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20 shadow-sm">
              <Wand2 className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold tracking-tight">Set your inputs</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Persona + product required. Offer optional. We'll pick 30 random ad templates for you.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label
              className="flex items-center gap-2 rounded-full border border-border/70 bg-background/60 backdrop-blur shadow-sm px-3 h-9 cursor-pointer select-none"
              data-testid="toggle-include-logo-wrap"
            >
              <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[11px] font-medium text-muted-foreground">Include my logo</span>
              <Switch
                checked={includeLogo}
                onCheckedChange={setIncludeLogo}
                disabled={!!session}
                data-testid="switch-include-logo"
              />
            </label>
            <div className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60 backdrop-blur shadow-sm" data-testid="toggle-aspect-ratio">
              {AD_ASPECT_RATIO_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  size="sm"
                  variant={aspectRatio === opt.value ? "default" : "ghost"}
                  className="h-7 px-3 text-[11px] gap-1 rounded-full"
                  onClick={() => { setAspectRatio(opt.value); saveLastAspectRatio(opt.value); }}
                  disabled={!!session}
                  data-testid={`toggle-aspect-${opt.value.replace(":", "x")}`}
                >
                  <Crop className="h-3 w-3" /> {opt.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-3">
          <PickerSelect
            icon={<Users className="h-4 w-4" />}
            label="Persona"
            placeholder="Choose persona"
            value={personaId}
            onChange={setPersonaId}
            options={(personas ?? []).map((p) => ({ value: p.id, label: p.name, imageUrl: (p as { imageUrl?: string | null }).imageUrl ?? null }))}
            testId="select-persona"
            searchable
            disabled={!brandId || !!session}
          />
          <PickerSelect
            icon={<Package className="h-4 w-4" />}
            label="Product"
            placeholder="Choose product"
            value={productId}
            onChange={setProductId}
            options={(products ?? []).map((p) => {
              const media = (p as { mediaJson?: string[] | null }).mediaJson;
              const first = Array.isArray(media) && media.length > 0 ? media[0] : null;
              return { value: p.id, label: p.name, imageUrl: first };
            })}
            testId="select-product"
            searchable
            disabled={!brandId || !!session}
          />
          {selectedProduct && !session && (
            <VariantPicker
              product={selectedProduct}
              value={selectedVariantName}
              onChange={setSelectedVariantName}
              testIdPrefix="tinder-variant"
            />
          )}
          <PickerSelect
            icon={<Tag className="h-4 w-4" />}
            label="Offer (optional)"
            placeholder={(offers ?? []).length === 0 ? "No offers — render at full price" : "No offer — render at full price"}
            value={offerId === "" ? "__none__" : offerId}
            onChange={(v) => setOfferId(v === "__none__" ? "" : v)}
            options={[
              { value: "__none__", label: "No offer — render at full price", description: null },
              ...((offers ?? []).map((o) => ({ value: o.id, label: o.name, description: (o as any).shortDescription || (o as any).summary || null }))),
            ]}
            testId="select-offer"
            searchable
            disabled={!brandId || !!session}
          />
        </div>

        <div className="relative flex items-center gap-2 pt-4 border-t border-dashed border-border/50 flex-wrap">
          {!routeBrandId && (
            <>
              <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground/70">Brand</span>
              <Select value={brandId} onValueChange={setBrandId} disabled={!!session}>
                <SelectTrigger className="h-7 text-xs w-52 border-transparent bg-transparent hover:bg-muted/50 focus:bg-background shadow-none px-2" data-testid="select-brand">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {(brands ?? []).map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-muted-foreground/40">·</span>
            </>
          )}
          <Folder className="h-3 w-3 text-muted-foreground/70" />
          <span className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground/70">Project</span>
          <Select value={projectId === "" ? "none" : projectId} onValueChange={(v) => setProjectId(v === "none" ? "" : v)} disabled={!!session}>
            <SelectTrigger className="h-7 text-xs w-52 border-transparent bg-transparent hover:bg-muted/50 focus:bg-background shadow-none px-2" data-testid="select-project">
              <SelectValue placeholder="(no project)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">(no project)</SelectItem>
              {(projects ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto flex items-center gap-2">
            {!session ? (
              <Button onClick={() => createSession.mutate()} disabled={!inputsReady || createSession.isPending || batchLoading} className="bg-violet-600 hover:bg-violet-700 h-8" data-testid="button-start">
                {(createSession.isPending || batchLoading) ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
                {(createSession.isPending || batchLoading) ? "Picking 30 templates…" : "Generate batch"}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => endSession.mutate()} disabled={endSession.isPending} size="sm" className="h-8" data-testid="button-end">
                <StopCircle className="h-3.5 w-3.5 mr-1.5" /> End session
              </Button>
            )}
          </div>
        </div>
      </Card>
      )}

      {/* Error panel */}
      {batchError && (
        <Card className="border-red-300 bg-red-50 dark:bg-red-950/30">
          <CardContent className="p-4 space-y-2">
            <div className="text-sm font-bold text-red-700 dark:text-red-300" data-testid="text-batch-error">Concept endpoint error</div>
            <div className="text-xs text-red-700 dark:text-red-200 whitespace-pre-wrap break-all">{batchError.message}</div>
            {batchError.endpoint && <div className="text-[11px] text-red-700/80 font-mono">POST {batchError.endpoint}</div>}
            {batchError.requestPayload && (
              <details className="text-xs"><summary className="cursor-pointer font-semibold">Request payload</summary>
                <pre className="mt-1 p-2 bg-white dark:bg-black/40 rounded text-[11px] overflow-auto max-h-72" data-testid="pre-error-payload">{JSON.stringify(batchError.requestPayload, null, 2)}</pre>
              </details>
            )}
            {batchError.rawResponse !== undefined && batchError.rawResponse !== null && (
              <details className="text-xs"><summary className="cursor-pointer font-semibold">Raw response</summary>
                <pre className="mt-1 p-2 bg-white dark:bg-black/40 rounded text-[11px] overflow-auto max-h-72" data-testid="pre-error-raw">{typeof batchError.rawResponse === "string" ? batchError.rawResponse : JSON.stringify(batchError.rawResponse, null, 2)}</pre>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loader while the batch is being seeded. */}
      {session && cards.length === 0 && batchLoading && !batchError && (
        <Card data-testid="card-batch-loading">
          <CardContent className="p-8 flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
            <div className="font-medium text-foreground">Picking 30 ad templates…</div>
            <div className="text-xs">Prompts and renders will stream in as they finish.</div>
          </CardContent>
        </Card>
      )}

      {session && cards.length > 0 && !reviewMode && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
          {/* LEFT — Phone mockup with a Meta-style ad */}
          <div className="flex flex-col items-center gap-4" data-testid="phone-stage">
            <div className="text-[11px] text-muted-foreground flex items-center gap-2">
              <span><strong className="text-foreground">{swipeQueue.length}</strong> ready</span>
              {renderingNow - queuedNow > 0 && <span className="text-violet-600 flex items-center gap-1" data-testid="text-rendering-count"><Loader2 className="h-3 w-3 animate-spin" /> {renderingNow - queuedNow} rendering</span>}
              {queuedNow > 0 && <span className="text-amber-600 flex items-center gap-1" data-testid="text-queued-count"><Clock className="h-3 w-3" /> {queuedNow} queued — starting shortly</span>}
              {totalPending === 0 && <span className="text-emerald-600">all swiped</span>}
              {debugBatch?.mock && <Badge variant="outline" className="text-[10px] text-violet-600">mock</Badge>}
            </div>

            <PhoneFrame>
              {currentCard ? (
                <SwipeableAd
                  key={currentCard.id}
                  card={currentCard}
                  brand={activeBrand ?? null}
                  persona={selectedPersona}
                  dragDx={dragDx}
                  exitDir={exitDir}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  underlay={nextCard ? <MetaAdContent card={nextCard} brand={activeBrand ?? null} persona={selectedPersona} dim aspectRatio={aspectRatio} showSafeZone={showSafeZone} /> : null}
                  aspectRatio={aspectRatio}
                  showSafeZone={showSafeZone}
                />
              ) : renderingNow > 0 ? (
                <PhoneEmptyState>
                  <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
                  <div className="text-sm font-medium">{queuedNow === renderingNow ? "Queued — starting shortly…" : "Rendering ads…"}</div>
                  <div className="text-[11px] text-muted-foreground">{queuedNow === renderingNow ? "Waiting for a render slot to free up." : "First one will land any second."}</div>
                </PhoneEmptyState>
              ) : totalPending === 0 ? (
                <PhoneEmptyState>
                  <div className="h-12 w-12 rounded-full bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center ring-1 ring-emerald-200 dark:ring-emerald-800">
                    <Check className="h-6 w-6 text-emerald-500" />
                  </div>
                  <div className="text-sm font-semibold">All done!</div>
                  <div className="text-[11px] text-muted-foreground text-center px-6">
                    You swiped every concept. {savedCards.length > 0 ? `You saved ${savedCards.length}.` : ""} Pick what's next below.
                  </div>
                </PhoneEmptyState>
              ) : (
                <PhoneEmptyState>
                  <Sparkles className="h-6 w-6 text-violet-500" />
                  <div className="text-sm font-medium">Preparing concepts…</div>
                  <div className="text-[11px] text-muted-foreground">Hang tight — building prompts.</div>
                </PhoneEmptyState>
              )}
            </PhoneFrame>

            {/* Swipe action buttons — swapped for the two-choice CTA once the
                deck is exhausted (nothing left to render or swipe). */}
            {totalPending === 0 && renderingNow === 0 ? (
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-md">
                <Button
                  onClick={generateMore}
                  disabled={batchLoading}
                  className="flex-1 w-full h-11 bg-violet-600 hover:bg-violet-700"
                  data-testid="button-generate-more"
                >
                  {batchLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Generate {batchSize} more
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setSelectedSetIds(new Set()); setReviewMode(true); }}
                  disabled={savedCards.length === 0}
                  className="flex-1 w-full h-11"
                  data-testid="button-review-sets"
                >
                  <Bookmark className="h-4 w-4 mr-2" />
                  Review &amp; create sets{savedCards.length > 0 ? ` (${savedCards.length})` : ""}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-6">
                <button
                  type="button"
                  onClick={() => swipe("left")}
                  disabled={!currentCard || !!exitDir}
                  className="h-14 w-14 rounded-full bg-card border border-rose-200 text-rose-500 shadow-md flex items-center justify-center hover:bg-rose-50 hover:scale-105 active:scale-95 transition disabled:opacity-40 disabled:hover:bg-card disabled:hover:scale-100"
                  data-testid="button-swipe-left"
                  aria-label="Reject"
                >
                  <X className="h-7 w-7" strokeWidth={2.5} />
                </button>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground select-none">
                  ← reject · save →
                </div>
                <button
                  type="button"
                  onClick={() => swipe("right")}
                  disabled={!currentCard || !!exitDir}
                  className="h-14 w-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-md flex items-center justify-center hover:scale-105 active:scale-95 transition disabled:opacity-40 disabled:hover:scale-100"
                  data-testid="button-swipe-right"
                  aria-label="Save"
                >
                  <Heart className="h-7 w-7" strokeWidth={2.5} fill="currentColor" />
                </button>
              </div>
            )}
          </div>

          {/* RIGHT — Saved concept rail */}
          <Card className="lg:sticky lg:top-4 self-start" data-testid="card-saved-rail">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold flex items-center gap-1.5">
                  <Bookmark className="h-4 w-4 text-emerald-500" />
                  Saved
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]" data-testid="badge-saved-count">
                    {savedCards.length}
                  </Badge>
                </h3>
                <span className="text-[10px] text-muted-foreground">{cards.length} total</span>
              </div>
              {savedCards.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border/60 px-3 py-8 text-center text-xs text-muted-foreground" data-testid="text-saved-empty">
                  Swipe right on an ad to save it here. Build sets of the directions you love.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-[720px] overflow-y-auto pr-1 -mr-1" data-testid="grid-saved">
                  {savedCards.map((c) => {
                    const concept = (c.concept as any) || {};
                    return (
                      <div
                        key={c.id}
                        className="group relative rounded-md overflow-hidden border bg-muted aspect-square"
                        data-testid={`tile-saved-${c.id}`}
                        title={concept.hook || concept.title || ""}
                      >
                        {c.render?.imageUrl ? (
                          <AdImage src={c.render.imageUrl} alt={concept.hook || ""} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/60">
                            <ImageIcon className="h-5 w-5" />
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-2 py-1.5 text-[10px] text-white leading-tight line-clamp-2">
                          {concept.hook || concept.title || "(saved)"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {debugBatch?.endpoint && (
                <div className="pt-2 border-t text-[10px] font-mono text-muted-foreground truncate">
                  POST {debugBatch.endpoint}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {session && cards.length > 0 && reviewMode && !setsMode && (
        <Card data-testid="card-review" className="border-border/60">
          <CardContent className="p-5 space-y-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-500 flex items-center justify-center ring-1 ring-emerald-200 dark:ring-emerald-800">
                  <Bookmark className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold tracking-tight">Review &amp; create sets</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {savedCards.length} saved · {selectedSetIds.size} selected for your set
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="ghost" size="sm" className="h-8" onClick={() => setReviewMode(false)} data-testid="button-back-to-swipe">
                  <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Back to deck
                </Button>
                <Button variant="outline" size="sm" className="h-8" onClick={generateMore} disabled={batchLoading} data-testid="button-review-generate-more">
                  {batchLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                  Generate {batchSize} more
                </Button>
                {isMaster && variationSets.length > 0 && (
                  <Button variant="outline" size="sm" className="h-8" onClick={() => setSetsMode(true)} data-testid="button-view-sets">
                    <LayoutGrid className="h-3.5 w-3.5 mr-1.5" /> View sets ({variationSets.length})
                  </Button>
                )}
              </div>
            </div>

            {savedCards.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap border-t border-dashed border-border/50 pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setSelectedSetIds(new Set(savedCards.map((c) => c.id)))}
                  data-testid="button-select-all"
                >
                  Select all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setSelectedSetIds(new Set())}
                  disabled={selectedSetIds.size === 0}
                  data-testid="button-clear-selection"
                >
                  Deselect all
                </Button>
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    disabled={selectedSetIds.size === 0}
                    onClick={() => {
                      const chosen = savedCards.filter((c) => selectedSetIds.has(c.id) && c.render?.imageUrl);
                      chosen.forEach((c, i) => {
                        const concept = (c.concept as any) || {};
                        downloadOne(c.render!.imageUrl!, concept.hook || concept.title || `set-${i + 1}`);
                      });
                      toast({ title: "Downloading set", description: `${chosen.length} ad${chosen.length === 1 ? "" : "s"}` });
                    }}
                    data-testid="button-download-set"
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    Download ({selectedSetIds.size})
                  </Button>
                  {isMaster && (
                    <Button
                      size="sm"
                      className="h-8 bg-violet-600 hover:bg-violet-700"
                      disabled={selectedSetIds.size === 0 || creatingSets}
                      onClick={createSets}
                      data-testid="button-create-sets"
                    >
                      {creatingSets ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <LayoutGrid className="h-3.5 w-3.5 mr-1.5" />}
                      Create sets ({selectedSetIds.size})
                    </Button>
                  )}
                </div>
              </div>
            )}

            {savedCards.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border/60 px-3 py-12 text-center text-sm text-muted-foreground" data-testid="text-review-empty">
                You didn't save any ads this round. Generate {batchSize} more to keep going.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="grid-review">
                {savedCards.map((c) => {
                  const concept = (c.concept as any) || {};
                  const selected = selectedSetIds.has(c.id);
                  return (
                    <div
                      key={c.id}
                      onClick={() => toggleSetSelection(c.id)}
                      className={`group relative rounded-xl overflow-hidden border bg-card cursor-pointer transition shadow-sm hover:shadow-md ${selected ? "ring-2 ring-violet-500 border-violet-400" : "border-border/60"}`}
                      data-testid={`card-review-${c.id}`}
                    >
                      <div className="relative aspect-square bg-muted">
                        {c.render?.imageUrl ? (
                          showSafeZone && aspectRatio === "9:16" ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                              <div className="relative h-full aspect-[9/16]">
                                <AdImage src={c.render.imageUrl} alt={concept.hook || ""} className="w-full h-full object-cover" loading="lazy" />
                                <SafeZoneOverlay />
                              </div>
                            </div>
                          ) : (
                            <AdImage src={c.render.imageUrl} alt={concept.hook || ""} className="w-full h-full object-cover" loading="lazy" />
                          )
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/60">
                            <ImageIcon className="h-6 w-6" />
                          </div>
                        )}
                        <div className={`absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center transition ${selected ? "bg-violet-600 text-white" : "bg-black/40 text-white/80 opacity-0 group-hover:opacity-100"}`}>
                          <Check className="h-3.5 w-3.5" strokeWidth={3} />
                        </div>
                      </div>
                      <div className="p-3 space-y-1">
                        <div className="text-xs font-medium leading-snug line-clamp-2" data-testid={`text-review-hook-${c.id}`}>
                          {concept.hook || concept.title || "(saved concept)"}
                        </div>
                        {c.render?.imageUrl && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); downloadOne(c.render!.imageUrl!, concept.hook || concept.title || "ad"); }}
                            className="text-[11px] text-violet-600 hover:text-violet-700 font-medium inline-flex items-center gap-1"
                            data-testid={`button-review-download-${c.id}`}
                          >
                            <Bookmark className="h-3 w-3" /> Download
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {session && setsMode && (
        <Card data-testid="card-sets" className="border-border/60">
          <CardContent className="p-5 space-y-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-violet-50 dark:bg-violet-950/40 text-violet-500 flex items-center justify-center ring-1 ring-violet-200 dark:ring-violet-800">
                  <LayoutGrid className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold tracking-tight">Variation sets</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {variationSets.length} set{variationSets.length === 1 ? "" : "s"} · each fills to 8 ads
                    {setsGenerating && <span className="ml-1 inline-flex items-center text-violet-500"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> generating…</span>}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="h-8" onClick={() => setSetsMode(false)} data-testid="button-back-to-review">
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to review
              </Button>
            </div>

            <VariationSetResults
              sets={variationSets}
              onSetsChange={setVariationSets}
              showSafeZone={showSafeZone}
              safeZoneOverlay={<SafeZoneOverlay />}
            />
          </CardContent>
        </Card>
      )}

    </div>
  );
}

function PickerSelect({
  icon, label, placeholder, value, onChange, options, testId, disabled, searchable,
}: {
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string; imageUrl?: string | null; description?: string | null }>;
  testId: string;
  disabled?: boolean;
  searchable?: boolean;
}) {
  const filled = !!value && value !== "__none__";
  const hasAvatars = options.some((o) => !!o.imageUrl);
  const selected = filled ? options.find((o) => o.value === value) : undefined;
  const selectedDescription = selected?.description?.trim() || "";
  const [open, setOpen] = useState(false);
  const { showSearch, query, setQuery, searchInputRef, isSearching, matchesLabel } =
    usePickerSearch(open, options.length, !!searchable);
  const filteredOptions = isSearching ? options.filter((o) => matchesLabel(o.label)) : options;
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled} open={open} onOpenChange={setOpen}>
      <SelectTrigger
        className={[
          "group h-auto min-h-[72px] rounded-xl px-3 py-2.5 transition-all duration-150",
          "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0",
          "disabled:opacity-60 disabled:cursor-not-allowed",
          filled
            ? "border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
            : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
        ].join(" ")}
        data-testid={testId}
      >
        <div className="flex items-center gap-3 w-full min-w-0">
          <div
            className={[
              "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ring-1",
              filled
                ? "bg-primary/15 text-primary ring-primary/20"
                : "bg-muted/70 text-muted-foreground ring-border/60 group-hover:text-foreground",
            ].join(" ")}
          >
            {icon}
          </div>
          <div className="flex flex-col items-start min-w-0 flex-1 text-left">
            <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70 leading-none mb-1.5 flex items-center gap-1">
              {label}
              {!filled && <span className="text-muted-foreground/40">*</span>}
            </span>
            {filled && selected ? (
              <div className="flex flex-col w-full min-w-0">
                <span className="text-sm truncate w-full leading-tight text-foreground font-semibold">
                  {selected.label}
                </span>
                {selectedDescription && (
                  <span className="text-[11px] text-muted-foreground/80 truncate w-full leading-tight mt-0.5">
                    {selectedDescription}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm truncate w-full leading-tight text-muted-foreground/60 font-normal">
                <SelectValue placeholder={placeholder} />
              </span>
            )}
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]">
        {showSearch && (
          <PickerSearchHeader
            inputRef={searchInputRef}
            query={query}
            onQueryChange={setQuery}
            testId={`input-search-${testId}`}
          />
        )}
        {showSearch && filteredOptions.length === 0 && <PickerNoMatches />}
        {filteredOptions.map((o) => {
          const desc = o.description?.trim() || "";
          return (
            <SelectItem key={o.value} value={o.value} className="text-xs">
              <div className="flex items-center gap-2 min-w-0">
                {hasAvatars ? (
                  o.imageUrl ? (
                    <img
                      src={thumbUrl(o.imageUrl, 64)!}
                      onError={thumbFallback(o.imageUrl)}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover shrink-0 border border-border/40"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0 border border-border/40 text-[10px] font-semibold text-muted-foreground">
                      {o.label.trim().charAt(0).toUpperCase() || "?"}
                    </div>
                  )
                ) : null}
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="truncate">{o.label}</span>
                  {desc && (
                    <span className="text-[11px] text-muted-foreground/80 truncate leading-tight mt-0.5">
                      {desc}
                    </span>
                  )}
                </div>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}


// -----------------------------------------------------------------------------
// PhoneFrame — iPhone-style chrome around the ad. Fixed width (~360px) so the
// 1:1 / 4:5 / 9:16 ad image always fits without horizontal scroll.
// -----------------------------------------------------------------------------
function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative w-[380px] max-w-full rounded-[44px] bg-neutral-900 p-3 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.05)] select-none"
      data-testid="phone-frame"
    >
      {/* Side buttons */}
      <span aria-hidden className="absolute left-[-2px] top-24 h-12 w-[3px] rounded-l bg-neutral-800" />
      <span aria-hidden className="absolute left-[-2px] top-40 h-16 w-[3px] rounded-l bg-neutral-800" />
      <span aria-hidden className="absolute right-[-2px] top-32 h-20 w-[3px] rounded-r bg-neutral-800" />
      <div className="relative rounded-[32px] overflow-hidden bg-white dark:bg-neutral-950">
        {/* Status bar */}
        <div className="flex items-center justify-between px-5 pt-2 pb-1 text-[11px] font-semibold text-neutral-900 dark:text-neutral-100 relative">
          <span>9:41</span>
          {/* Dynamic island */}
          <span aria-hidden className="absolute left-1/2 -translate-x-1/2 top-1 h-5 w-24 rounded-full bg-black" />
          <span className="flex items-center gap-1 text-[10px]">
            <span>●●●●</span>
            <span>5G</span>
            <span>100%</span>
          </span>
        </div>
        {/* Facebook-ish app header */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-neutral-200 dark:border-neutral-800">
          <span className="text-[20px] font-extrabold tracking-tight text-[#1877F2]">facebook</span>
          <div className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
            <div className="h-7 w-7 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            <div className="h-7 w-7 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
              <MessageCircle className="h-3.5 w-3.5" />
            </div>
          </div>
        </div>
        {/* Ad stage — fixed min height so empty states don't collapse */}
        <div className="relative min-h-[720px] bg-neutral-100 dark:bg-neutral-900" data-testid="phone-stage-inner">
          {children}
        </div>
      </div>
    </div>
  );
}

function PhoneEmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6">
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// SwipeableAd — the foreground card the user actually swipes. Handles transform
// (drag + rotate), exit animation, and renders the underlay (next card) behind
// it so the deck feels physical.
// -----------------------------------------------------------------------------
function SwipeableAd({
  card, brand, persona, dragDx, exitDir, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, underlay, aspectRatio, showSafeZone,
}: {
  card: CtCard;
  brand: Brand | null;
  persona: Persona | null;
  dragDx: number;
  exitDir: "left" | "right" | null;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  underlay: React.ReactNode;
  aspectRatio?: AspectRatio;
  showSafeZone?: boolean;
}) {
  const exiting = !!exitDir;
  const tx = exiting ? (exitDir === "right" ? 600 : -600) : dragDx;
  const rot = exiting ? (exitDir === "right" ? 18 : -18) : dragDx / 18;
  const opacity = exiting ? 0 : 1;
  const likeOpacity = Math.max(0, Math.min(1, dragDx / 110));
  const nopeOpacity = Math.max(0, Math.min(1, -dragDx / 110));

  return (
    <>
      {/* Underlay: next card peeking through */}
      {underlay && (
        <div className="absolute inset-0 opacity-60 scale-[0.97] origin-center" data-testid="phone-underlay">
          {underlay}
        </div>
      )}
      <div
        className="absolute inset-0 touch-none cursor-grab active:cursor-grabbing"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{
          transform: `translate3d(${tx}px,0,0) rotate(${rot}deg)`,
          opacity,
          transition: exiting || dragDx === 0 ? "transform 220ms ease-out, opacity 220ms ease-out" : "none",
        }}
        data-testid={`swipeable-card-${card.id}`}
      >
        <MetaAdContent card={card} brand={brand} persona={persona} aspectRatio={aspectRatio} showSafeZone={showSafeZone} />

        {/* SAVE / NOPE stamps */}
        <div
          aria-hidden
          className="absolute top-20 left-6 px-3 py-1.5 rounded-md border-4 border-emerald-500 text-emerald-500 font-extrabold tracking-widest text-xl rotate-[-18deg]"
          style={{ opacity: likeOpacity }}
        >
          SAVE
        </div>
        <div
          aria-hidden
          className="absolute top-20 right-6 px-3 py-1.5 rounded-md border-4 border-rose-500 text-rose-500 font-extrabold tracking-widest text-xl rotate-[18deg]"
          style={{ opacity: nopeOpacity }}
        >
          NOPE
        </div>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// MetaAdContent — the actual Facebook ad body (header, copy, image, CTA bar,
// reactions row). Stateless. `dim` is used when rendered as underlay.
// -----------------------------------------------------------------------------
function MetaAdContent({
  card, brand, persona, dim, aspectRatio, showSafeZone,
}: {
  card: CtCard;
  brand: Brand | null;
  persona: Persona | null;
  dim?: boolean;
  aspectRatio?: AspectRatio;
  showSafeZone?: boolean;
}) {
  const concept = (card.concept as any) || {};
  const hook = concept.hook || concept.title || "";
  const subcopy = concept.subcopy || "";
  const cta = concept.cta || "Learn more";
  const brandName = brand?.name || "Sponsored Page";
  const brandLogo = (brand as any)?.logoUrl || (persona as any)?.imageUrl || null;
  const siteHost = (() => {
    try { return brand?.url ? new URL(brand.url).host.replace(/^www\./, "") : "shop.example.com"; }
    catch { return "shop.example.com"; }
  })();

  return (
    <div className={`absolute inset-0 bg-white dark:bg-neutral-950 flex flex-col ${dim ? "pointer-events-none" : ""}`}>
      {/* Post header */}
      <div className="flex items-center gap-2 px-3 pt-3 pb-2">
        <div className="h-9 w-9 rounded-full bg-neutral-200 dark:bg-neutral-800 overflow-hidden shrink-0 flex items-center justify-center text-[11px] font-bold text-neutral-500">
          <ImgWithFallback
            src={brandLogo}
            className="h-full w-full object-cover"
            fallback={(brandName.trim().charAt(0) || "?").toUpperCase()}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-neutral-900 dark:text-neutral-100 truncate flex items-center gap-1">
            {brandName}
            <Check className="h-3 w-3 text-[#1877F2] shrink-0" strokeWidth={3} />
          </div>
          <div className="text-[11px] text-neutral-500 dark:text-neutral-400 flex items-center gap-1">
            <span>Sponsored</span> · <span>·</span> <span className="text-neutral-400">🌐</span>
          </div>
        </div>
        <MoreHorizontal className="h-4 w-4 text-neutral-500" />
        <X className="h-4 w-4 text-neutral-500" />
      </div>

      {/* Hook copy */}
      {hook && (
        <div className="px-3 pb-2 text-[13px] leading-snug text-neutral-900 dark:text-neutral-100 whitespace-pre-wrap" data-testid="text-ad-hook">
          {hook}
        </div>
      )}

      {/* Image. The phone mock previews the feed's 4:5 crop by default; when
          the safe-zone toggle is on for a 9:16 session, show the FULL 9:16
          frame with the band guides so users can verify the crop is safe. */}
      <div
        className={`relative w-full bg-neutral-100 dark:bg-neutral-900 overflow-hidden ${
          showSafeZone && aspectRatio === "9:16" ? "aspect-[9/16]" : "aspect-[4/5]"
        }`}
      >
        {card.render?.imageUrl ? (
          <AdImage
            src={card.render.imageUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
            data-testid={`img-ad-${card.id}`}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-neutral-400">
            <ImageIcon className="h-8 w-8" />
          </div>
        )}
        {showSafeZone && aspectRatio === "9:16" && card.render?.imageUrl && <SafeZoneOverlay />}
      </div>

      {/* CTA bar */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-neutral-100 dark:bg-neutral-900 border-y border-neutral-200 dark:border-neutral-800">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 truncate">{siteHost}</div>
          <div className="text-[13px] font-bold text-neutral-900 dark:text-neutral-100 truncate" data-testid="text-ad-subcopy">
            {subcopy || hook || brandName}
          </div>
        </div>
        <button
          type="button"
          tabIndex={-1}
          className="ml-3 shrink-0 rounded-md bg-neutral-200 dark:bg-neutral-800 px-3 py-1.5 text-[12px] font-semibold text-neutral-900 dark:text-neutral-100"
          data-testid="button-ad-cta"
        >
          {cta}
        </button>
      </div>

      {/* Reactions row */}
      <div className="flex items-center justify-between px-3 py-1.5 text-[11px] text-neutral-500 dark:text-neutral-400">
        <span className="flex items-center gap-1">
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#1877F2] text-white">
            <ThumbsUp className="h-2.5 w-2.5" fill="currentColor" />
          </span>
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-white">
            <Heart className="h-2.5 w-2.5" fill="currentColor" />
          </span>
          <span>1.2K</span>
        </span>
        <span>184 comments · 32 shares</span>
      </div>
      <div className="grid grid-cols-3 border-t border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium text-neutral-600 dark:text-neutral-300">
          <ThumbsUp className="h-4 w-4" /> Like
        </div>
        <div className="flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium text-neutral-600 dark:text-neutral-300">
          <MessageCircle className="h-4 w-4" /> Comment
        </div>
        <div className="flex items-center justify-center gap-1.5 py-2 text-[12px] font-medium text-neutral-600 dark:text-neutral-300">
          <Share2 className="h-4 w-4" /> Share
        </div>
      </div>
    </div>
  );
}
